import React from 'react';
import { RouteObject } from 'react-router-dom';
import { RedirectTo } from './components/routing/RedirectTo.component';
import { RouteConstants } from './route-constants';

// Re-exported for the many modules that already import it from here.
export { RouteConstants };

export interface Route {
    path: RouteConstants,
    element: React.ReactElement,
    /**
     * Nested routes for sections that render their own <Outlet /> — e.g. the
     * inventory manager, whose tab bar has to survive on its detail screens.
     */
    children?: ChildRoute[]
}

/**
 * Nested route whose path is not a top-level `RouteConstants` entry.
 *
 * Aliased to react-router's own `RouteObject` because that type is a
 * discriminated union on `index` — a hand-rolled interface with `index?: boolean`
 * is not assignable to it.
 */
export type ChildRoute = RouteObject;


const Home = React.lazy(() => import('./pages/home/Home.component'));
const SettingsHome = React.lazy(() => import('./pages/settings/home/SettingsHome.component'));
const Language = React.lazy(() => import('./pages/language/language/Language.component'));
const LanguageKey = React.lazy(() => import('./pages/language/translation-key/TranslationKey.component'));
const ManageChecklist = React.lazy(() => import('./pages/checklist/ManageChecklist.component'));
const Mockups = React.lazy(() => import('./pages/mockups/Mockups.component'));
const Mockup = React.lazy(() => import('./pages/mockups/Mockup.component'));
const BrandsHome = React.lazy(() => import('./pages/brands/brands.component'));
const BrandHome = React.lazy(() => import('./pages/brands/brand.component'));
const OrdersHome = React.lazy(() => import('./pages/orders/Orders/Orders.component'));
const OrderHome = React.lazy(() => import('./pages/orders/Order.component'));

const JDML = React.lazy(() => import('./pages/jdmls/JDMLHome.component'));
const JDMLSections = React.lazy(() => import('./pages/jdmls/jdml/JDML.component'));
const LineItem = React.lazy(() => import('./pages/line-items/line-items.component'));
const FailedOrder = React.lazy(() => import('./pages/orders/failed-order/failed-order.component'));
const CreateInternalOrder = React.lazy(() => import('./pages/internal_order/CreateInternalOrder.page'));
const InternalOrderStatus = React.lazy(() => import('./pages/internal_order/InternalOrderStatus.page'));
const Earnings = React.lazy(() => import('./pages/earnings/Earnings.page'));
const DashboardPricing = React.lazy(() => import('./pages/pricing/DashboardPricing.page'));
const Scanner = React.lazy(() => import('./pages/scanner/scanner.component'));
const Dropper = React.lazy(() => import('./pages/jdmls/dropper/dropper.component'));
const Users = React.lazy(() => import('./pages/users/users.component'));
const SalesInbox = React.lazy(() => import('./pages/sales/inbox/SalesInbox.component'));
const PeopleNext = React.lazy(() => import('./pages/sales/inbox-next/PeopleNext.component'));
const SalesMessages = React.lazy(() => import('./pages/sales/messages/SalesMessages.component'));
const SalesLeadDetail = React.lazy(() => import('./pages/sales/lead/SalesLeadDetail.component'));
const SalesTeam = React.lazy(() => import('./pages/sales/team/SalesTeam.component'));
const SalesTemplates = React.lazy(() => import('./pages/sales/templates/SalesTemplates.component'));
const SalesSignature = React.lazy(() => import('./pages/sales/signature/SalesSignature.component'));
const SalesCalendar = React.lazy(() => import('./pages/sales/calendar/SalesCalendar.component'));
const SalesTasks = React.lazy(() => import('./pages/sales/tasks/SalesTasks.component'));
const SalesPipeline = React.lazy(() => import('./pages/sales/pipeline/SalesPipeline.component'));
const MarketingLayout = React.lazy(() => import('./pages/marketing/shared/MarketingLayout.component'));
const MarketingOverview = React.lazy(() => import('./pages/marketing/overview/MarketingOverview.component'));
const MarketingContacts = React.lazy(() => import('./pages/marketing/contacts/MarketingContacts.component'));
const MarketingSegments = React.lazy(() => import('./pages/marketing/segments/MarketingSegments.component'));
const SegmentBuilder = React.lazy(() => import('./pages/marketing/segments/SegmentBuilder.component'));
const MarketingTemplates = React.lazy(() => import('./pages/marketing/templates/MarketingTemplates.component'));
const TemplateEditor = React.lazy(() => import('./pages/marketing/templates/TemplateEditor.component'));
const SystemEmails = React.lazy(() => import('./pages/marketing/templates/system/SystemEmails.component'));
const SystemEmailEditor = React.lazy(() => import('./pages/marketing/templates/system/SystemEmailEditor.component'));
const MarketingBrand = React.lazy(() => import('./pages/marketing/brand/MarketingBrand.component'));
const MarketingAssets = React.lazy(() => import('./pages/marketing/assets/MarketingAssets.component'));
const MarketingCampaigns = React.lazy(() => import('./pages/marketing/campaigns/MarketingCampaigns.component'));
const CampaignDetail = React.lazy(() => import('./pages/marketing/campaigns/CampaignDetail.component'));
const MarketingFlows = React.lazy(() => import('./pages/marketing/flows/MarketingFlows.component'));
const FlowDetail = React.lazy(() => import('./pages/marketing/flows/FlowDetail.component'));
const MarketingSettings = React.lazy(() => import('./pages/marketing/settings/MarketingSettings.component'));
const MarketingReview = React.lazy(() => import('./pages/marketing/review/MarketingReview.component'));
const MarketingSignals = React.lazy(() => import('./pages/marketing/signals/MarketingSignals.component'));
const MarketingLinks = React.lazy(() => import('./pages/marketing/links/MarketingLinks.component'));
const MarketingAttribution = React.lazy(() => import('./pages/marketing/attribution/MarketingAttribution.component'));
const Customers = React.lazy(() => import('./pages/customers/Customers.component'));
const Customer = React.lazy(() => import('./pages/customers/Customer.component'));
const Weight = React.lazy(() => import('./pages/weigh/WeighStation.component'));
const Shipping = React.lazy(() => import('./pages/shipping/Shipping.component'));
const Invoices = React.lazy(() => import('./pages/invoice/Invoices.component'));
const Invoice = React.lazy(() => import('./pages/invoice/Invoice.component'));
const WasteTracking = React.lazy(() => import('./pages/waste/WasteTracking.component'));
const Analytics = React.lazy(() => import('./pages/analytics/analytics.component'));
const BrandAnalytics = React.lazy(() => import('./pages/brand-analytics/BrandAnalytics.component'));
const FeatureFlags = React.lazy(() => import('./pages/feature_flags/FeatureFlags.component'));
const Announcements = React.lazy(() => import('./pages/announcements/Announcements.component'));
const AdminTools = React.lazy(() => import('./pages/admintools/AdminTools.component'));
// The admin-tools panels are named exports, so each is unwrapped into the
// default shape React.lazy expects.
const named = <T extends string>(loader: () => Promise<any>, exportName: T) =>
    React.lazy(() => loader().then((module: any) => ({ default: module[exportName] })));

const OrderManagement = named(() => import('./pages/admintools/order-management/OrderManagement.component'), 'OrderManagement');
const BrandAllocation = named(() => import('./pages/admintools/brand-allocation/BrandAllocation.component'), 'BrandAllocation');
const ProductionTools = named(() => import('./pages/admintools/production-tools/ProductionTools.component'), 'ProductionTools');
const CarrierManagement = named(() => import('./pages/admintools/carrier-management/CarrierManagement.component'), 'CarrierManagement');
const PrinterManagement = named(() => import('./pages/admintools/printer-management/PrinterManagement.component'), 'PrinterManagement');
const BoxLabelImages = named(() => import('./pages/admintools/box-label-images/BoxLabelImages.component'), 'BoxLabelImages');
const Diagnostics = named(() => import('./pages/admintools/diagnostics/Diagnostics.component'), 'Diagnostics');
const WebhookQueue = named(() => import('./pages/admintools/webhook-queue/WebhookQueue.component'), 'WebhookQueue');
const ReleaseNotesAdmin = named(() => import('./pages/admintools/release-notes/ReleaseNotesAdmin.component'), 'ReleaseNotesAdmin');
const ApiAccessRequests = named(() => import('./pages/admintools/api-access-requests/ApiAccessRequests.component'), 'ApiAccessRequests');
const SocialPromptEditor = named(() => import('./pages/admintools/social-prompts/SocialPromptEditor.component'), 'SocialPromptEditor');
const ErrorReports = named(() => import('./pages/admintools/error-reports/ErrorReports.component'), 'ErrorReports');
const ReferenceRates = named(() => import('./pages/admintools/reference-rates/ReferenceRates.component'), 'ReferenceRates');
const AccountTypePricing = named(() => import('./pages/admintools/account-type-pricing/AccountTypePricing.component'), 'AccountTypePricing');
const PluginSubscriptionRate = named(() => import('./pages/admintools/plugin-subscription-rate/PluginSubscriptionRate.component'), 'PluginSubscriptionRate');
const DescriptionBannedWords = named(() => import('./pages/admintools/description-banned-words/DescriptionBannedWords.component'), 'DescriptionBannedWords');
const ArtistInfluencers = named(() => import('./pages/admintools/artist-influencers/ArtistInfluencers.component'), 'ArtistInfluencers');
const DesignHistory = named(() => import('./pages/admintools/design-history/DesignHistory.component'), 'DesignHistory');
// Inventory Management pages
const InventoryLayout = React.lazy(() => import('./pages/inventory/InventoryLayout.component'));
const InventoryHome = React.lazy(() => import('./pages/inventory/inventory/Inventory.component'));
const InventoryOverview = React.lazy(() => import('./pages/inventory/overview/InventoryOverview.component'));
const InventoryVendors = React.lazy(() => import('./pages/inventory/vendors/Vendors.component'));
const InventoryStockTypes = React.lazy(() => import('./pages/inventory/stock_types/StockTypes.component'));
const InventoryUnitTypes = React.lazy(() => import('./pages/inventory/unit_types/UnitTypes.component'));
const InventoryZones = React.lazy(() => import('./pages/inventory/zones/Zones.component'));
const InventoryTrackableItems = React.lazy(() => import('./pages/inventory/trackable_items/TrackableItems.component'));
const InventoryTrackableItem = React.lazy(() => import('./pages/inventory/trackable_items/TrackableItemDetails.component'));
const InventoryPurchaseOrders = React.lazy(() => import('./pages/inventory/purchase_orders/PurchaseOrders.component'));
const InventoryPurchaseOrder = React.lazy(() => import('./pages/inventory/purchase_orders/PurchaseOrderDetail.component'));
const InventoryAuditGroups = React.lazy(() => import('./pages/inventory/audit_groups/AuditGroups.component'));
const InventoryAuditGroup = React.lazy(() => import('./pages/inventory/audit_groups/AuditGroupItems.component'));
const DesignCreation = React.lazy(() => import('./pages/design_creator/DesignCreatorProject.component'));
const DesignerCatalog = React.lazy(() => import('./pages/designer_catalog/DesignerCatalog.component'));
const SampleBundles = React.lazy(() => import('./pages/sample_bundles/SampleBundles.page'));
const CreateSampleBundle = React.lazy(() => import('./pages/sample_bundles/CreateSampleBundle.page'));
const ProductListing = React.lazy(() => import('./pages/product_listing/ProductListing.component'));
const LicensedDesigns = React.lazy(() => import('./pages/product_listing/LicensedDesigns.component'));
const LicensingSetup = React.lazy(() => import('./pages/product_listing/LicensingSetup.component'));
const Inspo = React.lazy(() => import('./pages/inspo/Inspo.component'));
const InteriorProjects = React.lazy(() => import('./pages/interior_projects/InteriorProjects.component'));
const InteriorBranding = React.lazy(() => import('./pages/interior_branding/InteriorBranding.component'));
const InteriorProjectDetail = React.lazy(() => import('./pages/interior_projects/InteriorProjectDetail.component'));
const DesignCreationDesign = React.lazy(() => import('./pages/design_creator/DesignCreatorDesignDetailPageV2.component'));
const MockupTemplatesPage = React.lazy(() => import('./pages/design_creator/MockupTemplatesPage.component'));
const CancellationsPage = React.lazy(() => import('./pages/cancellations/Cancellations.component'));
const ReleaseNotes = React.lazy(() => import('./pages/release-notes/ReleaseNotes.component'));
const MaterialGuide = React.lazy(() => import('./pages/material_guide/MaterialGuide.component'));

/**
 * Inventory's previous home. Registered as /admin-tools children so a stale
 * bookmark redirects into the Production group with its params intact.
 * The bare ADMIN_TOOLS_INVENTORY entry is declared LAST so react-router does
 * not let its shorter path shadow the more specific ones.
 */
const movedAdminInventoryRoutes: ChildRoute[] = [
    { path: RouteConstants.ADMIN_TOOLS_INVENTORY_VENDORS, element: <RedirectTo to={RouteConstants.PRODUCTION_INVENTORY_VENDORS} /> },
    { path: RouteConstants.ADMIN_TOOLS_INVENTORY_STOCK_TYPES, element: <RedirectTo to={RouteConstants.PRODUCTION_INVENTORY_STOCK_TYPES} /> },
    { path: RouteConstants.ADMIN_TOOLS_INVENTORY_UNIT_TYPES, element: <RedirectTo to={RouteConstants.PRODUCTION_INVENTORY_UNIT_TYPES} /> },
    { path: RouteConstants.ADMIN_TOOLS_INVENTORY_ZONES, element: <RedirectTo to={RouteConstants.PRODUCTION_INVENTORY_ZONES} /> },
    { path: RouteConstants.ADMIN_TOOLS_INVENTORY_TRACKABLE_ITEM, element: <RedirectTo to={RouteConstants.PRODUCTION_INVENTORY_TRACKABLE_ITEM} /> },
    { path: RouteConstants.ADMIN_TOOLS_INVENTORY_TRACKABLE_ITEMS, element: <RedirectTo to={RouteConstants.PRODUCTION_INVENTORY_TRACKABLE_ITEMS} /> },
    { path: RouteConstants.ADMIN_TOOLS_INVENTORY_PURCHASE_ORDER, element: <RedirectTo to={RouteConstants.PRODUCTION_INVENTORY_PURCHASE_ORDER} /> },
    { path: RouteConstants.ADMIN_TOOLS_INVENTORY_PURCHASE_ORDERS, element: <RedirectTo to={RouteConstants.PRODUCTION_INVENTORY_PURCHASE_ORDERS} /> },
    { path: RouteConstants.ADMIN_TOOLS_INVENTORY_AUDIT_GROUP, element: <RedirectTo to={RouteConstants.PRODUCTION_INVENTORY_AUDIT_GROUP} /> },
    { path: RouteConstants.ADMIN_TOOLS_INVENTORY_AUDIT_GROUPS, element: <RedirectTo to={RouteConstants.PRODUCTION_INVENTORY_AUDIT_GROUPS} /> },
    { path: RouteConstants.ADMIN_TOOLS_INVENTORY, element: <RedirectTo to={RouteConstants.PRODUCTION_INVENTORY} /> }
];

/**
 * Everything that moved under /admin-tools. These are registered as CHILDREN of
 * the /admin-tools route (see Router.tsx) so the admin-tools left navigation
 * keeps rendering around them — including on detail screens.
 */
export const adminToolsRoutes: ChildRoute[] = [
    // Sections that always lived here.
    { path: '/admin-tools/order-management', element: <OrderManagement /> },
    { path: '/admin-tools/brand-allocation', element: <BrandAllocation /> },
    { path: '/admin-tools/production-tools', element: <ProductionTools /> },
    { path: '/admin-tools/carriers', element: <CarrierManagement /> },
    { path: '/admin-tools/printers', element: <PrinterManagement /> },
    { path: '/admin-tools/box-label-images', element: <BoxLabelImages /> },
    { path: '/admin-tools/diagnostics', element: <Diagnostics /> },
    { path: '/admin-tools/webhook-queue', element: <WebhookQueue /> },
    { path: '/admin-tools/release-notes', element: <ReleaseNotesAdmin /> },
    { path: '/admin-tools/api-access-requests', element: <ApiAccessRequests /> },
    { path: '/admin-tools/social-prompts', element: <SocialPromptEditor /> },
    { path: '/admin-tools/error-reports', element: <ErrorReports /> },
    { path: '/admin-tools/reference-rates', element: <ReferenceRates /> },
    { path: '/admin-tools/account-type-pricing', element: <AccountTypePricing /> },
    { path: '/admin-tools/plugin-subscription-rate', element: <PluginSubscriptionRate /> },
    { path: '/admin-tools/description-banned-words', element: <DescriptionBannedWords /> },
    { path: '/admin-tools/artist-influencers', element: <ArtistInfluencers /> },
    { path: RouteConstants.ADMIN_TOOLS_DESIGN_HISTORY, element: <DesignHistory /> },
    { path: RouteConstants.ADMIN_TOOLS_DESIGN_HISTORY_DESIGN, element: <DesignHistory /> },

    // Sections moved out of the SUPERADMIN sidebar.
    { path: RouteConstants.ADMIN_TOOLS_MOCKUPS, element: <Mockups /> },
    { path: RouteConstants.ADMIN_TOOLS_MOCKUP, element: <Mockup /> },
    { path: RouteConstants.ADMIN_TOOLS_BRAND_ANALYTICS, element: <BrandAnalytics /> },
    { path: RouteConstants.ADMIN_TOOLS_TRANSLATIONS, element: <Language /> },
    { path: RouteConstants.ADMIN_TOOLS_TRANSLATION_KEYS, element: <LanguageKey /> },
    { path: RouteConstants.ADMIN_TOOLS_MANAGE_CHECKLIST, element: <ManageChecklist /> },
    { path: RouteConstants.ADMIN_TOOLS_FEATURE_FLAGS, element: <FeatureFlags /> },
    { path: RouteConstants.ADMIN_TOOLS_ANNOUNCEMENTS, element: <Announcements /> },
    // Inventory MOVED to the Production nav group (/production/inventory).
    // These entries stay registered as children of /admin-tools purely so old
    // bookmarks redirect instead of falling through to the dashboard.
    ...movedAdminInventoryRoutes,
    { path: RouteConstants.ADMIN_TOOLS_JDML, element: <JDML /> },
    { path: RouteConstants.ADMIN_TOOLS_JDML_SECTIONS, element: <JDMLSections /> },
    { path: RouteConstants.ADMIN_TOOLS_DROPPER, element: <Dropper /> },
    { path: RouteConstants.ADMIN_TOOLS_ANALYTICS, element: <Analytics /> },
    { path: RouteConstants.ADMIN_TOOLS_CANCELLATIONS, element: <CancellationsPage /> }
];

/**
 * Pre-move paths. Bookmarks, emailed links, and anything a user typed from
 * muscle memory must land on the new page rather than the dashboard fallback,
 * so every relocated path redirects with its params intact.
 */
const movedRoutes: Route[] = [
    { path: RouteConstants.TRANSLATIONS, element: <RedirectTo to={RouteConstants.ADMIN_TOOLS_TRANSLATIONS} /> },
    { path: RouteConstants.TRANSLATION_KEYS, element: <RedirectTo to={RouteConstants.ADMIN_TOOLS_TRANSLATION_KEYS} /> },
    { path: RouteConstants.MANAGE_CHECKLIST, element: <RedirectTo to={RouteConstants.ADMIN_TOOLS_MANAGE_CHECKLIST} /> },
    { path: RouteConstants.MOCKUPS, element: <RedirectTo to={RouteConstants.ADMIN_TOOLS_MOCKUPS} /> },
    { path: RouteConstants.MOCKUP, element: <RedirectTo to={RouteConstants.ADMIN_TOOLS_MOCKUP} /> },
    { path: RouteConstants.JDML, element: <RedirectTo to={RouteConstants.ADMIN_TOOLS_JDML} /> },
    { path: RouteConstants.JDML_SECTIONS, element: <RedirectTo to={RouteConstants.ADMIN_TOOLS_JDML_SECTIONS} /> },
    { path: RouteConstants.DROPPER, element: <RedirectTo to={RouteConstants.ADMIN_TOOLS_DROPPER} /> },
    { path: RouteConstants.ANALYTICS, element: <RedirectTo to={RouteConstants.ADMIN_TOOLS_ANALYTICS} /> },
    { path: RouteConstants.BRAND_ANALYTICS, element: <RedirectTo to={RouteConstants.ADMIN_TOOLS_BRAND_ANALYTICS} /> },
    { path: RouteConstants.FEATURE_FLAGS, element: <RedirectTo to={RouteConstants.ADMIN_TOOLS_FEATURE_FLAGS} /> },
    { path: RouteConstants.ANNOUNCEMENTS, element: <RedirectTo to={RouteConstants.ADMIN_TOOLS_ANNOUNCEMENTS} /> },
    { path: RouteConstants.CANCELLATIONS, element: <RedirectTo to={RouteConstants.ADMIN_TOOLS_CANCELLATIONS} /> },
    { path: RouteConstants.INVENTORY_HOME, element: <RedirectTo to={RouteConstants.PRODUCTION_INVENTORY} /> },
    { path: RouteConstants.INVENTORY_VENDORS, element: <RedirectTo to={RouteConstants.PRODUCTION_INVENTORY_VENDORS} /> },
    { path: RouteConstants.INVENTORY_STOCK_TYPES, element: <RedirectTo to={RouteConstants.PRODUCTION_INVENTORY_STOCK_TYPES} /> },
    { path: RouteConstants.INVENTORY_UNIT_TYPES, element: <RedirectTo to={RouteConstants.PRODUCTION_INVENTORY_UNIT_TYPES} /> },
    { path: RouteConstants.INVENTORY_ZONES, element: <RedirectTo to={RouteConstants.PRODUCTION_INVENTORY_ZONES} /> },
    { path: RouteConstants.INVENTORY_TRACKABLE_ITEMS, element: <RedirectTo to={RouteConstants.PRODUCTION_INVENTORY_TRACKABLE_ITEMS} /> },
    { path: RouteConstants.INVENTORY_TRACKABLE_ITEM, element: <RedirectTo to={RouteConstants.PRODUCTION_INVENTORY_TRACKABLE_ITEM} /> },
    { path: RouteConstants.INVENTORY_PURCHASE_ORDERS, element: <RedirectTo to={RouteConstants.PRODUCTION_INVENTORY_PURCHASE_ORDERS} /> },
    { path: RouteConstants.INVENTORY_PURCHASE_ORDER, element: <RedirectTo to={RouteConstants.PRODUCTION_INVENTORY_PURCHASE_ORDER} /> },
    { path: RouteConstants.INVENTORY_AUDIT_GROUPS, element: <RedirectTo to={RouteConstants.PRODUCTION_INVENTORY_AUDIT_GROUPS} /> },
    { path: RouteConstants.INVENTORY_AUDIT_GROUP, element: <RedirectTo to={RouteConstants.PRODUCTION_INVENTORY_AUDIT_GROUP} /> }
];

export const dashboardRoutes: Route[] = [
    { path: RouteConstants.HOME, element: <Home /> },
    { path: RouteConstants.SETTINGS_HOME, element: <SettingsHome /> },
    { path: RouteConstants.BRANDS, element: <BrandsHome /> },
    { path: RouteConstants.BRAND, element: <BrandHome /> },
    { path: RouteConstants.ORDERS, element: <OrdersHome /> },
    // Must be registered before ORDER — react-router picks the more specific
    // static segment, but keeping the order explicit avoids a future surprise.
    { path: RouteConstants.INTERNAL_ORDER_NEW, element: <CreateInternalOrder /> },
    { path: RouteConstants.INTERNAL_ORDER_STATUS, element: <InternalOrderStatus /> },
    { path: RouteConstants.EARNINGS, element: <Earnings /> },
    { path: RouteConstants.PRICING_GUIDE, element: <DashboardPricing /> },
    { path: RouteConstants.ORDER, element: <OrderHome /> },

    { path: RouteConstants.LINE_ITEM, element: <LineItem /> },
    { path: RouteConstants.FAILED_ORDER, element: <FailedOrder /> },
    { path: RouteConstants.SCANNER, element: <Scanner /> },
    { path: RouteConstants.USERS, element: <Users /> },
    // People: the distilled view is the default; the classic one stays one tap away.
    { path: RouteConstants.SALES_INBOX, element: <PeopleNext /> },
    { path: RouteConstants.SALES_INBOX_NEXT, element: <PeopleNext /> },
    { path: RouteConstants.SALES_INBOX_CLASSIC, element: <SalesInbox /> },
    { path: RouteConstants.SALES_MESSAGES, element: <SalesMessages /> },
    { path: RouteConstants.SALES_LEAD, element: <SalesLeadDetail /> },
    { path: RouteConstants.SALES_TEAM, element: <SalesTeam /> },
    { path: RouteConstants.SALES_TEMPLATES, element: <SalesTemplates /> },
    { path: RouteConstants.SALES_SIGNATURE, element: <SalesSignature /> },
    { path: RouteConstants.SALES_CALENDAR, element: <SalesCalendar /> },
    { path: RouteConstants.SALES_TASKS, element: <SalesTasks /> },
    { path: RouteConstants.SALES_PIPELINE, element: <SalesPipeline /> },
    { path: RouteConstants.CUSTOMERS, element: <Customers /> },
    { path: RouteConstants.CUSTOMER, element: <Customer /> },
    { path: RouteConstants.WEIGH_STATION, element: <Weight /> },
    { path: RouteConstants.SHIP_STATION, element: <Shipping /> },
    { path: RouteConstants.INVOICES, element: <Invoices /> },
    { path: RouteConstants.INVOICE, element: <Invoice /> },
    { path: RouteConstants.WASTE_TRACKING, element: <WasteTracking /> },
    { path: RouteConstants.DESIGN_CREATION, element: <DesignCreation /> },
    { path: RouteConstants.DESIGNER_CATALOG, element: <DesignerCatalog /> },
    // More specific first — '/sample-bundles/new' must not be swallowed by a future param route.
    { path: RouteConstants.CREATE_SAMPLE_BUNDLE, element: <CreateSampleBundle /> },
    { path: RouteConstants.SAMPLE_BUNDLES, element: <SampleBundles /> },
    { path: RouteConstants.PRODUCT_LISTING, element: <ProductListing /> },
    { path: RouteConstants.LICENSED_DESIGNS, element: <LicensedDesigns /> },
    { path: RouteConstants.LICENSING_SETUP, element: <LicensingSetup /> },
    { path: RouteConstants.INSPO, element: <Inspo /> },
    // Same page, scoped to one owner brand via the :brandId param.
    { path: RouteConstants.PRODUCT_LISTING_BRAND, element: <ProductListing /> },
    { path: RouteConstants.INTERIOR_PROJECTS, element: <InteriorProjects /> },
    { path: RouteConstants.INTERIOR_PROJECT_DETAIL, element: <InteriorProjectDetail /> },
    { path: RouteConstants.INTERIOR_BRANDING, element: <InteriorBranding /> },
    { path: RouteConstants.DESIGN_CREATION_DESIGN, element: <DesignCreationDesign /> },
    { path: RouteConstants.DESIGN_CREATION_MOCKUPS, element: <MockupTemplatesPage /> },
    { path: RouteConstants.RELEASE_NOTES, element: <ReleaseNotes /> },
    // Registered well before RouteConstants.ANY ('*'), which renders <Home />.
    { path: RouteConstants.MATERIAL_GUIDE, element: <MaterialGuide /> },

    // The whole inventory manager is a SINGLE Production sub-tab; its own tab bar
    // (InventoryLayout) navigates between the screens below. The index route is
    // the at-a-glance stock overview; the barcode intake console has its own tab.
    {
        path: RouteConstants.PRODUCTION_INVENTORY,
        element: <InventoryLayout />,
        children: [
            { index: true, element: <InventoryOverview /> },
            { path: RouteConstants.PRODUCTION_INVENTORY_INTAKE, element: <InventoryHome /> },
            { path: RouteConstants.PRODUCTION_INVENTORY_VENDORS, element: <InventoryVendors /> },
            { path: RouteConstants.PRODUCTION_INVENTORY_STOCK_TYPES, element: <InventoryStockTypes /> },
            { path: RouteConstants.PRODUCTION_INVENTORY_UNIT_TYPES, element: <InventoryUnitTypes /> },
            { path: RouteConstants.PRODUCTION_INVENTORY_ZONES, element: <InventoryZones /> },
            { path: RouteConstants.PRODUCTION_INVENTORY_TRACKABLE_ITEMS, element: <InventoryTrackableItems /> },
            { path: RouteConstants.PRODUCTION_INVENTORY_TRACKABLE_ITEM, element: <InventoryTrackableItem /> },
            { path: RouteConstants.PRODUCTION_INVENTORY_PURCHASE_ORDERS, element: <InventoryPurchaseOrders /> },
            { path: RouteConstants.PRODUCTION_INVENTORY_PURCHASE_ORDER, element: <InventoryPurchaseOrder /> },
            { path: RouteConstants.PRODUCTION_INVENTORY_AUDIT_GROUPS, element: <InventoryAuditGroups /> },
            { path: RouteConstants.PRODUCTION_INVENTORY_AUDIT_GROUP, element: <InventoryAuditGroup /> }
        ]
    },

    // Email marketing: MarketingLayout guards sales access, loads the workspace once
    // and shows the test-mode / finish-setup banners above every marketing page.
    {
        path: RouteConstants.MARKETING,
        element: <MarketingLayout />,
        children: [
            { index: true, element: <MarketingOverview /> },
            { path: RouteConstants.MARKETING_CONTACTS, element: <MarketingContacts /> },
            { path: RouteConstants.MARKETING_SEGMENTS, element: <MarketingSegments /> },
            { path: RouteConstants.MARKETING_SEGMENT, element: <SegmentBuilder /> },
            { path: RouteConstants.MARKETING_TEMPLATES, element: <MarketingTemplates /> },
            { path: RouteConstants.MARKETING_TEMPLATE, element: <TemplateEditor /> },
            { path: RouteConstants.MARKETING_SYSTEM_EMAILS, element: <SystemEmails /> },
            { path: RouteConstants.MARKETING_SYSTEM_EMAIL, element: <SystemEmailEditor /> },
            { path: RouteConstants.MARKETING_BRAND, element: <MarketingBrand /> },
            { path: RouteConstants.MARKETING_ASSETS, element: <MarketingAssets /> },
            { path: RouteConstants.MARKETING_CAMPAIGNS, element: <MarketingCampaigns /> },
            { path: RouteConstants.MARKETING_CAMPAIGN, element: <CampaignDetail /> },
            { path: RouteConstants.MARKETING_REVIEW, element: <MarketingReview /> },
            { path: RouteConstants.MARKETING_SIGNALS, element: <MarketingSignals /> },
            { path: RouteConstants.MARKETING_FLOWS, element: <MarketingFlows /> },
            { path: RouteConstants.MARKETING_FLOW, element: <FlowDetail /> },
            { path: RouteConstants.MARKETING_SETTINGS, element: <MarketingSettings /> },
            { path: RouteConstants.MARKETING_LINKS, element: <MarketingLinks /> },
            { path: RouteConstants.MARKETING_ATTRIBUTION, element: <MarketingAttribution /> }
        ]
    },

    ...movedRoutes,

    // The catch-all is ranked last by react-router regardless of position, but it
    // is kept at the end so the file reads the way the router behaves.
    { path: RouteConstants.ANY, element: <Home /> }
];

export { AdminTools };